import { ChoiceModel } from '@next/core-lib/table-v2';
import { SelectedLocation } from '@next/core-lib/user';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

interface ShipToOption {
  id: number;
  externalShipToId: string;
}

interface DateRange {
  startDate: Date;
  endDate: Date;
}

export const mapLocationOptions = (
  locations: SelectedLocation[],
  shipTos: ShipToOption[],
): ChoiceModel<number>[] => {
  const options = new Map<number, ChoiceModel<number>>();

  locations?.forEach((location) =>
    options.set(location.id, {
      value: location.id,
      viewValue: `${location.street}, ${location.postalCode}, ${location.city} (${location.externalId})`,
    }),
  );

  shipTos.forEach((shipTo) => {
    if (!shipTo.id || options.has(shipTo.id)) {
      return;
    }

    options.set(shipTo.id, {
      value: shipTo.id,
      viewValue: shipTo.externalShipToId,
    });
  });

  return [...options.values()];
};

export const createLocationOptions = <T extends ShipToOption>(
  locations$: Observable<SelectedLocation[]>,
  dateRangeChanges$: Observable<DateRange | null>,
  initialDateRange: DateRange,
  getShipTos: (startDate: string, endDate: string) => Observable<T[]>,
): Observable<ChoiceModel<number>[]> =>
  combineLatest([
    locations$,
    dateRangeChanges$.pipe(
      startWith(initialDateRange),
      map((dateRange) => dateRange ?? initialDateRange),
      distinctUntilChanged(
        (previous, current) =>
          previous.startDate?.getTime() === current.startDate?.getTime() &&
          previous.endDate?.getTime() === current.endDate?.getTime(),
      ),
      switchMap((dateRange) =>
        getShipTos(dateRange.startDate.toISOString(), dateRange.endDate.toISOString()),
      ),
    ),
  ]).pipe(
    map(([locations, shipTos]) => mapLocationOptions(locations, shipTos)),
    shareReplay(1),
  );
